export class PlatformError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, any>;
  public readonly requestId: string;
  public readonly timestamp: string;

  constructor(
    message: string,
    code: string,
    status: number,
    details?: Record<string, any>,
    requestId?: string,
  ) {
    super(message);
    this.name = 'PlatformError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId || '';
    this.timestamp = new Date().toISOString();
  }
}

export class PlatformAuthError extends PlatformError {
  constructor(message: string, requestId?: string) {
    super(message, 'AUTH_ERROR', 401, undefined, requestId);
    this.name = 'PlatformAuthError';
  }
}

export class PlatformPermissionError extends PlatformError {
  constructor(message: string, requestId?: string) {
    super(message, 'PERMISSION_ERROR', 403, undefined, requestId);
    this.name = 'PlatformPermissionError';
  }
}

export class PlatformNotFoundError extends PlatformError {
  constructor(message: string, requestId?: string) {
    super(message, 'NOT_FOUND', 404, undefined, requestId);
    this.name = 'PlatformNotFoundError';
  }
}

export class PlatformValidationError extends PlatformError {
  constructor(message: string, details?: Record<string, any>, requestId?: string) {
    super(message, 'VALIDATION_ERROR', 422, details, requestId);
    this.name = 'PlatformValidationError';
  }
}

export class PlatformTimeoutError extends PlatformError {
  constructor(requestId?: string) {
    super('Request timed out', 'TIMEOUT_ERROR', 408, undefined, requestId);
    this.name = 'PlatformTimeoutError';
  }
}

export class PlatformNetworkError extends PlatformError {
  constructor(message: string, requestId?: string) {
    super(message, 'NETWORK_ERROR', 0, undefined, requestId);
    this.name = 'PlatformNetworkError';
  }
}

export class PlatformInternalError extends PlatformError {
  constructor(message: string, requestId?: string) {
    super(message, 'INTERNAL_ERROR', 500, undefined, requestId);
    this.name = 'PlatformInternalError';
  }
}

export function isPlatformError(error: unknown): error is PlatformError {
  return error instanceof PlatformError;
}

export function parsePlatformError(error: unknown): PlatformError {
  if (error instanceof PlatformError) {
    return error;
  }
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response: { status: number; data: any } };
    const status = axiosError.response.status;
    const data = axiosError.response.data;
    const message = data?.message || data?.error || 'An unexpected error occurred';
    const requestId = data?.requestId || '';
    switch (status) {
      case 401:
        return new PlatformAuthError(message, requestId);
      case 403:
        return new PlatformPermissionError(message, requestId);
      case 404:
        return new PlatformNotFoundError(message, requestId);
      case 422:
        return new PlatformValidationError(message, data?.details, requestId);
      case 408:
        return new PlatformTimeoutError(requestId);
      case 500:
        return new PlatformInternalError(message, requestId);
      default:
        return new PlatformError(message, 'UNKNOWN_ERROR', status, undefined, requestId);
    }
  }
  if (error instanceof Error && error.message.includes('timeout')) {
    return new PlatformTimeoutError();
  }
  return new PlatformNetworkError(
    error instanceof Error ? error.message : 'Network error',
  );
}

export class PlatformErrorHandler {
  handleRequestError(error: unknown): PlatformError {
    return parsePlatformError(error);
  }

  handleResponseError(error: unknown): PlatformError {
    return parsePlatformError(error);
  }
}